import React from 'react';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Popper from '@material-ui/core/Popper';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import LettercardSelector from '../components/LetterCardSelect';
import RadioGroup from '@material-ui/core/RadioGroup';
import Grid from '@material-ui/core/Grid';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import { useTranslation } from 'react-i18next';
import PopoverWrap from '../../Popover';
import Button from '@material-ui/core/Button';
import { StudentLoginType } from 'types/schema-generated-types';
import CancelIcon from '@material-ui/icons/Cancel';

import { SecretCodeSelectProps } from '../types';

const useStyles = makeStyles<Theme>((theme: Theme) => {
  return {
    popper: {
      zIndex: theme.zIndex.modal,
      backgroundColor: theme.palette.common.white,
      padding: theme.spacing(3),
      boxShadow: theme.shadows[6]
    },
    clearAllButton: {
      float: 'right',
      marginRight: theme.spacing(2)
    },
    radio: {
      marginRight: 0
    },
    secretCodeLabel: {
      display: 'inline-block',
      marginTop: theme.spacing(0.75)
    },
    textRadioContainer: {
      paddingTop: theme.spacing(2)
    },
    radioGroup: {
      minWidth: '500px !important'
    },
    textBoxContainer: {
      width: '50%',
      paddingBottom: theme.spacing(2)
    },
    endButtonContainer: {
      float: 'right'
    },
    helperText: {
      marginTop: theme.spacing(2)
    },
    cancelButton: {
      marginRight: theme.spacing(3)
    }
  };
});

const SecretCodeSelect: React.FC<SecretCodeSelectProps> = ({
  children,
  disabled,
  maxTextBoxLength = 16,
  showUnselected = true,
  handleClose: onClose,
  handleConfirm: onConfirm,
  loginType = StudentLoginType.LETTER_CARDS,
  onSecretCodeChange,
  anchorEl = null,
  open = false,
  placement = 'bottom-start',
  secretCode = '',
  ...rest
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  //Todo: Should be changed to use a reducer
  const [internalLoginType, setLoginType] = React.useState(loginType);
  const [internalAnchorEl, setAnchorEl] = React.useState(anchorEl);
  const [toolTipOpen, setToolTipOpen] = React.useState(false);
  const [errors, setErrors] = React.useState({ textBox: '', letterBox: '' });

  const [secretCodeValues, setSecretCodeValues] = React.useState(
    internalLoginType === StudentLoginType.LETTER_CARDS
      ? { textBox: '', letterBox: secretCode }
      : { textBox: secretCode, letterBox: '' }
  );

  const handleTextBoxChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    if (e.target.value.length <= maxTextBoxLength) {
      if (/^[A-Z0-9]+$/i.test(e.target.value) || e.target.value === '') {
        setSecretCodeValues({ ...secretCodeValues, textBox: e.target.value.toUpperCase() });
        setErrors({ textBox: '', letterBox: '' });
        if (onSecretCodeChange) {
          onSecretCodeChange(internalLoginType, e.target.value.toUpperCase());
        }
      }
    }
  };
  const handleLetterBoxChange: (value: string) => void = (value: string) => {
    setSecretCodeValues({ ...secretCodeValues, letterBox: value });
    setErrors({ textBox: '', letterBox: '' });
    if (onSecretCodeChange) {
      onSecretCodeChange(internalLoginType, value);
    }
  };
  const handleSelectionClear: React.MouseEventHandler<HTMLSpanElement> = () => {
    setSecretCodeValues({ textBox: '', letterBox: '' });
    setErrors({ textBox: '', letterBox: '' });
    if (onSecretCodeChange) {
      onSecretCodeChange(internalLoginType, '');
    }
  };

  const handleLoginTypeChange: React.ChangeEventHandler<HTMLInputElement> = event => {
    const value = event.currentTarget.value as StudentLoginType;
    setLoginType(value);
    if (onSecretCodeChange) {
      onSecretCodeChange(
        value,
        value === StudentLoginType.LETTER_CARDS
          ? secretCodeValues.letterBox
          : secretCodeValues.textBox
      );
    }
  };
  const handleClose = React.useCallback((): void => {
    setAnchorEl(null);
    onClose();
  }, [setAnchorEl, onClose]);

  const handleClickAway = React.useCallback((): void => {
    if (!toolTipOpen) {
      handleClose();
    }
  }, [toolTipOpen, handleClose]);
  const checkErrors = React.useCallback((): boolean => {
    if (internalLoginType === StudentLoginType.LETTER_CARDS) {
      if (secretCodeValues.letterBox.length < 2) {
        setErrors({
          letterBox: t('Letter card secret codes must have at least two letter cards.'),
          textBox: ''
        });
        return false;
      }
    }
    if (internalLoginType === StudentLoginType.TEXTBOX) {
      if (
        secretCodeValues.textBox.length < 2 ||
        secretCodeValues.textBox.length > 16 ||
        !/^[A-Z0-9]+$/i.test(secretCodeValues.textBox)
      ) {
        setErrors({
          textBox: t(
            'Text box secret codes must be 2-16 characters and contain only letters and numbers.'
          ),
          letterBox: ''
        });
        return false;
      }
    }
    return true;
  }, [internalLoginType, secretCodeValues, setErrors, t]);
  const handleConfirm = React.useCallback(() => {
    if (checkErrors()) {
      if (onConfirm) {
        onConfirm(
          internalLoginType,
          internalLoginType === StudentLoginType.LETTER_CARDS
            ? secretCodeValues.letterBox
            : secretCodeValues.textBox
        );
      }
      handleClose();
    }
  }, [handleClose, internalLoginType, onConfirm, secretCodeValues, checkErrors]);

  if (anchorEl && !internalAnchorEl) {
    setLoginType(loginType);
    setSecretCodeValues(
      internalLoginType === StudentLoginType.LETTER_CARDS
        ? { textBox: '', letterBox: secretCode }
        : { textBox: secretCode, letterBox: '' }
    );
    setAnchorEl(anchorEl);
  }

  return (
    <Popper open={open} anchorEl={anchorEl} placement={placement} className={classes.popper}>
      <ClickAwayListener onClickAway={handleClickAway} mouseEvent={false}>
        {
          <div>
            <RadioGroup
              className={classes.radioGroup}
              onChange={handleLoginTypeChange}
              value={internalLoginType}
              {...rest}
            >
              <Grid container>
                <Grid item xs={7}>
                  <PopoverWrap
                    onOpen={(): void => setToolTipOpen(true)}
                    onClose={(): void => setToolTipOpen(false)}
                    title={t('Letter Cards')}
                    description={
                      <>
                        {t('Students will enter their secret code using Lettercards or by typing.')}
                        <div className={classes.helperText}>
                          {t('(Typically used for beginners)')}
                        </div>
                      </>
                    }
                  >
                    <FormControlLabel
                      classes={{ root: classes.radio }}
                      value={StudentLoginType.LETTER_CARDS}
                      control={<Radio color="secondary" />}
                      label={t('Letter Cards')}
                    />
                  </PopoverWrap>
                </Grid>
                <Grid item xs={5}>
                  <Typography className={classes.secretCodeLabel}>{t('Secret Code')}</Typography>
                  <Button
                    variant="text"
                    size="small"
                    disableElevation
                    startIcon={<CancelIcon />}
                    className={classes.clearAllButton}
                    onClick={!disabled ? handleSelectionClear : undefined}
                  >
                    {t('Clear All')}
                  </Button>
                </Grid>
              </Grid>
              {(internalLoginType === StudentLoginType.LETTER_CARDS || showUnselected) && (
                <div>
                  <LettercardSelector
                    displayDirection="row"
                    error={errors.letterBox && errors.letterBox}
                    secretCode={secretCodeValues.letterBox}
                    onChange={handleLetterBoxChange}
                    disabled={internalLoginType !== StudentLoginType.LETTER_CARDS}
                  />
                </div>
              )}
              <div className={classes.textRadioContainer}>
                <PopoverWrap
                  title={t('Text Box')}
                  onOpen={(): void => setToolTipOpen(true)}
                  description={
                    <>
                      {t('Students will enter their secret code using a textbox.')}
                      <div className={classes.helperText}>
                        {t(
                          'Secret code is not case sensitive, should be 2-16 characters long and only contain letters and numbers.'
                        )}
                      </div>
                    </>
                  }
                >
                  <FormControlLabel
                    value={StudentLoginType.TEXTBOX}
                    classes={{ root: classes.radio }}
                    control={<Radio color="secondary" />}
                    label={t('Text Box')}
                  />
                </PopoverWrap>
              </div>
              {(internalLoginType === StudentLoginType.TEXTBOX || showUnselected) && (
                <div className={classes.textBoxContainer}>
                  <TextField
                    disabled={internalLoginType !== StudentLoginType.TEXTBOX}
                    variant="outlined"
                    size="small"
                    helperText={errors.textBox && errors.textBox}
                    fullWidth
                    placeholder={t('Secret Code')}
                    value={secretCodeValues.textBox}
                    name="secretCode"
                    onChange={handleTextBoxChange}
                  />
                </div>
              )}
              {children}
            </RadioGroup>
            <div className={classes.endButtonContainer}>
              <Button
                className={classes.cancelButton}
                variant="outlined"
                color="secondary"
                size="small"
                disableElevation
                onClick={handleClose}
              >
                {t('Cancel')}
              </Button>
              <Button
                className={classes.confirmButton}
                color="primary"
                variant="contained"
                size="small"
                disableElevation
                onClick={handleConfirm}
              >
                {t('Save Secret Code')}
              </Button>
            </div>
          </div>
        }
      </ClickAwayListener>
    </Popper>
  );
};

export default SecretCodeSelect;
